import L, { type LatLng } from "leaflet";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Marker, Pane, Polygon, Polyline } from "react-leaflet";
import { connect } from "react-redux";
import { v4 as uuid } from "uuid";

import actions from "@/actions";
import type { LightGroupGeometryModel } from "@/types/entity-types";
import { Geometry } from "@/utils";

import { DrawLightGroup, LightGroupGeometry } from ".";

// replace the default leaflet icon
L.Marker.prototype.options.icon = L.divIcon({
    className: "foo", // need this to prevent default leaflet div rendering
    html:
        '<div style="display:flex; align-items:center; justify-content:center; background-color:gray; height:14px; width:14px; z-index:176; border-radius:10px" >' +
        '<div style="display:flex; align-items:center; justify-content:center; background-color:white; height:10px; width:10px; z-index:176; border-radius:10px">' +
        "</div></div>",
});

const PANE_NAME = "editMarkers";
const Z_INDEX = 1000;

interface EditVertex {
    id: string;
    latLng: LatLng;
}

interface EditLightGroupProps {
    geometry: LightGroupGeometryModel;
    setEditingGroupGeometry: (geometry: LightGroupGeometryModel | null) => void;
    setPendingGroupGeometry: (geometry: LightGroupGeometryModel | null) => void;
}

const EditLightGroup = ({
    geometry,
    setEditingGroupGeometry,
    setPendingGroupGeometry,
}: EditLightGroupProps): ReactNode => {
    const getVerticesFromGeometry = (geom): EditVertex[] =>
        geom.vertices
            .slice(0, -1)
            .map((latLng: LatLng) => ({ id: uuid(), latLng }));

    const [error, setError] = useState(false);

    const [vertices, setVertices] = useState(() =>
        getVerticesFromGeometry(geometry),
    );

    const [previousVertices, setPreviousVertices] = useState(() =>
        getVerticesFromGeometry(geometry),
    );

    // Sync vertices state when geometry prop changes
    useEffect(() => {
        const newVertices = getVerticesFromGeometry(geometry);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setVertices(newVertices);
        setPreviousVertices(newVertices);
    }, [geometry]);

    useEffect(
        () => () => {
            setEditingGroupGeometry(null);
            setPendingGroupGeometry(null);
        },
        [setEditingGroupGeometry, setPendingGroupGeometry],
    );

    const errorFlash = useCallback(() => {
        setError(true);
        setTimeout(() => setError(false), DrawLightGroup.ERROR_FLASH_MS);
    }, []);

    const onVertexDragStart = useCallback(() => {
        setPreviousVertices(vertices);
    }, [vertices]);

    const onVertexDrag = useCallback((vertexId, e) => {
        setVertices((v) =>
            v.map((vertex) =>
                vertex.id === vertexId
                    ? { ...vertex, latLng: e.latlng }
                    : vertex,
            ),
        );
    }, []);

    const onVertexDragEnd = useCallback(() => {
        setVertices((currentVertices) => {
            const poly = [
                ...currentVertices.map(({ latLng }) => latLng),
                currentVertices[0].latLng,
            ];

            const invalid =
                poly.length > DrawLightGroup.MIN_VERTICES + 1 &&
                Geometry.containsIntersections(Geometry.latLngToArray(poly));

            if (invalid) {
                errorFlash();
                return previousVertices;
            }

            // Schedule the Redux action after render to avoid updating during render
            setTimeout(() => {
                setPendingGroupGeometry({
                    ...geometry,
                    vertices: poly,
                });
            }, 0);

            return currentVertices;
        });
    }, [errorFlash, previousVertices, setPendingGroupGeometry, geometry]);

    const onVertexDoubleClick = useCallback(
        (vertexId) => {
            setVertices((currentVertices) => {
                if (currentVertices.length <= DrawLightGroup.MIN_VERTICES) {
                    errorFlash();
                    return currentVertices;
                }

                return currentVertices.filter(({ id }) => id !== vertexId);
            });
        },
        [errorFlash],
    );

    const onEdgeClick = useCallback(
        ([start, end], startId) => {
            setVertices((currentVertices) => {
                if (currentVertices.length >= DrawLightGroup.MAX_VERTICES) {
                    errorFlash();
                    return currentVertices;
                }

                const midLat = (start.lat + end.lat) / 2;
                const midLng = (start.lng + end.lng) / 2;
                const startIndex = currentVertices.findIndex(
                    ({ id }) => id === startId,
                );

                const newVertices = [...currentVertices];

                newVertices.splice(startIndex + 1, 0, {
                    id: uuid(),
                    latLng: L.latLng(midLat, midLng),
                });
                return newVertices;
            });
        },
        [errorFlash],
    );

    const renderPolygon = () => {
        const color = error ? DrawLightGroup.ERROR_COLOR : geometry.group.color;

        const edges = vertices.map((vertex, i) => {
            const nextVertex = vertices[i + 1] ?? vertices[0];

            const endpoints: [LatLng, LatLng] = [
                vertex.latLng,
                nextVertex.latLng,
            ];

            return (
                <Polyline
                    key={vertex.id}
                    positions={endpoints}
                    pathOptions={{
                        weight: LightGroupGeometry.POLY_WEIGHT,
                        color,
                        fill: true,
                        fillColor: color,
                        fillOpacity: LightGroupGeometry.POLY_OPACITY_HOVER,
                    }}
                    interactive
                    eventHandlers={{
                        click: () => onEdgeClick(endpoints, vertex.id),
                    }}
                />
            );
        });

        const fill = (
            <Polygon
                key="fill"
                positions={[
                    ...vertices.map(({ latLng }) => latLng),
                    vertices[0].latLng,
                ]}
                pathOptions={{
                    weight: LightGroupGeometry.POLY_WEIGHT,
                    color,
                    fill: true,
                    fillColor: color,
                    fillOpacity: LightGroupGeometry.POLY_OPACITY_NEUTRAL,
                }}
                interactive={false}
            />
        );

        return [edges, fill];
    };

    const renderVertices = () => {
        return vertices.map((vertex) => (
            <Marker
                key={vertex.id}
                position={vertex.latLng}
                draggable
                eventHandlers={{
                    dragstart: onVertexDragStart,
                    drag: (e) => onVertexDrag(vertex.id, e),
                    dragend: onVertexDragEnd,
                    dblclick: () => onVertexDoubleClick(vertex.id),
                }}
                pane={PANE_NAME}
            />
        ));
    };

    return (
        <>
            <Pane name={PANE_NAME} style={{ zIndex: Z_INDEX }} />
            {renderPolygon()}
            {renderVertices()}
        </>
    );
};

EditLightGroup.PANE_NAME = PANE_NAME;
EditLightGroup.Z_INDEX = Z_INDEX;

const mapStateToProps = () => ({});

const mapDispatchToProps = {
    setEditingGroupGeometry: actions.map.general.setEditingGroupGeometry,
    setPendingGroupGeometry: actions.map.general.setPendingGroupGeometry,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditLightGroup);
